import { Ng2StateDeclaration, UIRouterGlobals, UIRouterModule } from '@uirouter/angular';
import { BaseLayoutComponent } from '@client/app/layout/base.component';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { SessionService } from '@monsido/core/session/session.service';
import { LoginService } from '@client/app/core/services/login/login.service';
import { AgreementRepoService } from '@client/app/services/api/agreement-repo/agreement-repo.service';
import { CoreAgreementService } from '@monsido/core/session/services/agreement/agreement.service';
import { Rejection, StateService, Transition, UrlService } from '@uirouter/core';
import { DomainRepoService } from '@client/app/services/api/domain-repo/domain-repo.service';
import { CoreDomainService } from '@client/app/core/services/domain/core-domain.service';
import { DomainGroupRepoService } from '@client/app/services/api/domain-groups-repo/domain-group-repo.service';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { MonLoadingService } from '@monsido/services/loading/services/loading.service';
import { CustomerPickerComponent } from '@client/app/pages/customer-picker/customer-picker.component';
import { OverlayService } from '@monsido/services/overlay-service/overlay.service';
import { CoreError } from '@client/app/core/enums/core-error.enum';
import { StatisticsModule } from '@client/app/pages/statistics/statistics.module';
import { CommonModule } from '@angular/common';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { Agreement } from '@monsido/modules/models/api/agreement';
import { asyncExecute } from '@client/app/core/utils/async-execute';
import { LangDefinition, TranslocoService } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { resetAccessibilityStateRegistryForCustomerContext } from '@client/app/layout/utils/accessibility-state-registry-reset.util';

@Component({
    selector: 'base-customer',
    template: `
        <ui-view></ui-view>`,
    standalone: true,
    imports: [
        UIRouterModule,
    ],
})
class BaseCustomerComponent {}


@Component({
    selector: 'base-domain',
    template: `
        <statistics-cap-text *ngIf="showCaptext"></statistics-cap-text>
        <div data-legacy-content-scope
             [attr.data-uses-modern-styles]="usesModernStyles ? 'true' : 'false'">
            <ui-view name="content"></ui-view>
        </div>
    `,
    standalone: true,
    imports: [
        CommonModule,
        UIRouterModule,
        StatisticsModule,
    ],
})
class BaseDomainComponent implements OnInit {
    urlService = inject(UrlService);
    destroyRef = inject(DestroyRef);
    overlayService = inject(OverlayService);
    sessionService = inject(SessionService);
    pipeService = inject(PipeService);
    uiRouterGlobals = inject(UIRouterGlobals);

    previousParams = '';
    overlayIsOpen = false;
    showCaptext = false;
    usesModernStyles = false;

    ngOnInit () {
        this.setShowStatisticsCapText();
        this.setUsesModernStyles();
        const deRegisterHook = this.urlService.onChange(() => {
            this.processRouteChangeForOverlay();
            this.setShowStatisticsCapText();
            this.setUsesModernStyles();
        });
        this.processRouteChangeForOverlay();
        this.destroyRef.onDestroy(() => {
            deRegisterHook();
        });
    }

    setShowStatisticsCapText () {
        const isStatsRoute = this.uiRouterGlobals.current?.name?.includes('base.customer.domain.statistics');
        if (isStatsRoute && this.sessionService.domain !== null) {
            this.showCaptext = !this.sessionService.domain.monsido_script_installed && !this.pipeService.get('isStatsSampleData')(this.sessionService.domain);
        } else {
            this.showCaptext = false;
        }
    }

    processRouteChangeForOverlay (): void {
        const queryParams = this.urlService.search();
        const paramsAsString = JSON.stringify(queryParams);

        // TODO: add a handler for multiple overlays here
        if (queryParams.issueOverlay) {
            if (paramsAsString !== this.previousParams) {
                if (this.previousParams.includes('issueOverlay=true')) {
                    delete queryParams.issueOverlay;
                }
                this.overlayIsOpen = true;
                this.overlayService.openOverlay(queryParams);
            }
        } else if (this.overlayIsOpen) {
            this.overlayService.closeAllDialogs();
            this.overlayIsOpen = false;
        }

        this.previousParams = paramsAsString;
    };

    private setUsesModernStyles (): void {
        this.usesModernStyles = this.uiRouterGlobals.current?.data?.usesModernStyles === true;
    }
}

async function loadDefaultAndActiveTranslations (translocoService: TranslocoService): Promise<void> {
    const defaultLang = translocoService.getDefaultLang() || 'en';
    const availableLangs = translocoService.getAvailableLangs().map((lang: string | LangDefinition) =>
        typeof lang === 'string' ? lang : lang.id,
    );

    const activeLang = (translocoService.getActiveLang() || defaultLang)
        .toLowerCase();

    const activeBaseLang = activeLang.split(/[-_]/)[0];
    const safeActiveLang = availableLangs.includes(activeBaseLang)
        ? activeBaseLang
        : defaultLang;

    if (safeActiveLang === defaultLang) {
        await lastValueFrom(translocoService.load(defaultLang));
        return;
    }

    await Promise.all([
        lastValueFrom(translocoService.load(defaultLang)),
        lastValueFrom(translocoService.load(safeActiveLang)),
    ]);
}

let registeredListenerIds: number[] = [];
export const LAYOUT_STATES: Ng2StateDeclaration[] = [
    {
        name: 'base',
        url: '',
        data: { anonymous_access: false },
        component: BaseLayoutComponent,
        onEnter: async (transition) => {
            const injector = transition.injector();
            const translocoService: TranslocoService = injector.get(TranslocoService);
            // Ensure English translations are loaded before starting the router
            await loadDefaultAndActiveTranslations(translocoService);

            const ng2MonEventsService: MonEventService = injector.get(MonEventService);
            const monDialog = injector.get(MonDialog);

            const closeAllDialogsListenerId = ng2MonEventsService.addListener(MON_EVENTS.CLOSE_ALL_DIALOGS, (index) => {
                monDialog.closeAll();
                return index;
            });
            registeredListenerIds.push(closeAllDialogsListenerId);

            const loadDialogListenerId = ng2MonEventsService.addListener(MON_EVENTS.LOAD_DIALOG, (options) => {

                const { params, callback, onBeforeClose } = options;
                const paramsCopy = {
                    ...params,
                    manualFocus: true,
                    parentSelector: '#common-dialog-wrapper',
                };

                return monDialog.create(paramsCopy, callback, onBeforeClose);
            });
            registeredListenerIds.push(loadDialogListenerId);
        },
        onExit: (transition) => {
            const injector = transition.injector();
            const ng2MonEventsService: MonEventService = injector.get(MonEventService);
            try {
                for (const listenerId of registeredListenerIds) {
                    ng2MonEventsService.remove(listenerId);
                }
            } finally {
                registeredListenerIds = [];
            }
        },
    },
    {
        name: 'base.dashboard',
        url: '/',
        views: {
            'home@base': {
                component: CustomerPickerComponent,
            },
        },
        data: {
            anonymous_access: false,
            title: 'Acquia Web Governance - Customer Picker',
        },
    },
    {
        name: 'base.customer',
        url: '/{customerId}',
        abstract: true,
        views: {
            'customer@base': {
                component: BaseCustomerComponent,
            },
        },
        params: {
            customerId: { dynamic: true },
        },
        data: { anonymous_access: false, title: 'Acquia Web Governance' },
        resolve: [{
            token: 'validateCustomer',
            policy: { when: 'EAGER' },
            deps: [
                SessionService,
                LoginService,
                AgreementRepoService,
                CoreAgreementService,
                StateService,
                Transition,
                MonLoadingService,
            ],
            resolveFn: async (
                ng2SessionService: SessionService,
                ng2CoreLoginService: LoginService,
                ng2AgreementRepoService: AgreementRepoService,
                coreAgreementService: CoreAgreementService,
                $state: StateService,
                transition: Transition,
                monLoadingService: MonLoadingService,
            ) => {
                const $stateParams = transition.params();
                monLoadingService.show();
                const setAgreement = async (agreement: Agreement, agreementCollection: Agreement[]) => {
                    await coreAgreementService.setAgreement(agreement);
                    coreAgreementService.setAgreements(agreementCollection);
                };

                if (!ng2CoreLoginService.isLoggedIn()) {
                    // Abort the transition to prevent redundant API calls on entering further states
                    // User should be redirected to login page from the transition hook in basic.run
                    transition.abort();
                    throw new Error(CoreError.UserIsNotLoggedIn);
                }

                const [getUserError, user] = await asyncExecute(ng2CoreLoginService.getUser(), () => new Error(CoreError.UserIsNotLoggedIn));
                if (getUserError) {
                    const to = transition.to();
                    if (to.name !== 'login.login') {
                        transition.abort();
                    }
                    // Do nothing since it will be handled by the login state
                    return;
                }

                ng2SessionService.setMe(user);
                resetAccessibilityStateRegistryForCustomerContext(transition);

                if (user && ng2SessionService.customer && ng2SessionService.customer.id === parseInt($stateParams.customerId)) {
                    // Do nothing when the customerId in State Params is the same as in SessionService
                    return;
                }

                const [getAgreementsError, agreements] = await asyncExecute(ng2AgreementRepoService.getAll(), () => new Error(CoreError.CustomerNotFound));
                if (getAgreementsError) {
                    throw Rejection.redirected($state.target('base.dashboard'));
                }

                for (let i = 0; i < agreements.length; i++) {
                    if (agreements[i].account.id === parseInt($stateParams.customerId)) {
                        await setAgreement(agreements[i], agreements);
                        return;
                    }
                }

                throw Rejection.redirected($state.target('base.dashboard'));
            },
        }],
        onExit: function (transition) {
            const fromParams = transition.params('from');
            const toParams = transition.params('to');
            if (fromParams.customerId === toParams.customerId) {
                return;
            }
            return transition.injector().get(MonEventService).run('exitCustomer');
        },
        onEnter: async (transition) => {
            const to = transition.to();
            const injector = transition.injector();
            const stateService: StateService = injector.get(StateService);
            const ng2MonEventsService: MonEventService = injector.get(MonEventService);
            try {
                await ng2MonEventsService.run('afterCustomer');
                await ng2MonEventsService.run('finishCustomer', { customerId: transition.params().customerId });
            } catch {
            }

            // HACK: Redirect to customer dashboard if users access host/{customerId} URL directly.
            // Can't think of a solution for now. Most likely we'll migrate to the built-in Angular Router anyway.
            if (to.name === 'base.customer') {
                stateService.go('base.customer.dashboard', transition.params());
            }
        },
    },
    {
        name: 'base.customer.domain',
        url: '/domains/{domainId}?domain_group&issueOverlayIssueId&issueOverlayContentId&issueOverlayIssueType&issueOverlayPageId&issueOverlayPageLinkId&issueOverlay&issueOverlayCheckId&issueOverlaySourceCodeId&time',
        component: BaseDomainComponent,
        params: {
            domainId: { value: '', dynamic: true },
            domain_group: { value: null, dynamic: true },
            issueOverlayIssueId: { dynamic: true },
            issueOverlayContentId: { dynamic: true },
            issueOverlayIssueType: { dynamic: true },
            issueOverlayPageId: { dynamic: true },
            issueOverlayPageLinkId: { dynamic: true },
            issueOverlayCheckId: { dynamic: true },
            issueOverlaySourceCodeId: { dynamic: true },
            issueOverlay: { dynamic: true },
            time: { dynamic: true },
        },
        abstract: true,
        data: { anonymous_access: false, topmenu: 'default' },
        resolve: [
            {
                token: 'validateDomain',
                policy: { when: 'EAGER' },
                deps: [
                    SessionService,
                    DomainRepoService,
                    StateService,
                    CoreDomainService,
                    DomainGroupRepoService,
                    LoginService,
                    MonLoadingService,
                    Transition,
                    'validateCustomer', // Ensure validateCustomer is resolved before domain
                ],
                resolveFn: async (
                    ng2SessionService: SessionService,
                    ng2DomainRepoService: DomainRepoService,
                    stateService: StateService,
                    ng2CoreDomainService: CoreDomainService,
                    ng2DomainGroupRepoService: DomainGroupRepoService,
                    ng2CoreLoginService: LoginService,
                    monLoadingService: MonLoadingService,
                    transition: Transition,
                ) => {
                    const $stateParams = transition.params();
                    monLoadingService.show();

                    if (!ng2CoreLoginService.isLoggedIn()) {
                        // Abort the transition to prevent redundant API calls on entering further states
                        // User should be redirected to login page from the transition hook in basic.run
                        transition.abort();
                        throw (new Error(CoreError.UserIsNotLoggedIn));
                    }

                    // Null-safe domain_group comparison: when the URL carries
                    // no domain_group param we only require domainId to match.
                    // The previous check (`domainGroup?.id.toString() === domain_group`)
                    // evaluated to `undefined === null` → false, forcing a full
                    // repo refetch on every cross-domain CTA even when the
                    // domainId already matched the session domain.
                    const sessionDomainId = ng2SessionService.domain?.id?.toString();
                    const sessionDomainGroupId = ng2SessionService.domainGroup?.id?.toString();
                    const domainIdMatches = sessionDomainId === $stateParams.domainId;
                    const domainGroupMatches =
                        $stateParams.domain_group == null
                            ? true
                            : sessionDomainGroupId === $stateParams.domain_group;

                    if (domainIdMatches && domainGroupMatches) {
                        // DomainId (and DomainGroupId when present) in the URL
                        // parameters match SessionService — no refetch needed.
                        return;
                    }

                    try {
                        const domain = await ng2DomainRepoService.get($stateParams.domainId, { exclude: ['domain_groups'] });
                        ng2CoreDomainService.setDomain(domain);

                        if (!$stateParams.domain_group) {
                            // No DomainGroupId query parameter, we are done here.
                            // Notify the domain has been changed and out.
                            ng2CoreDomainService.changeDomain();
                            return;
                        }

                        // Get DomainGroup details
                        const group = await ng2DomainGroupRepoService.get(domain.id, $stateParams.domain_group);
                        ng2CoreDomainService.setDomainGroup(group as any);
                        ng2CoreDomainService.changeDomain();
                    } catch {
                        throw Rejection.redirected(stateService.target('base.dashboard'));
                    }
                },
            },
        ],
        onExit: async function (transition) {
            const injector = transition.injector();
            const monDialog = injector.get(MonDialog);
            monDialog.closeAll(undefined, true);
        },
        onEnter: async function (transition) {
            const injector = transition.injector();
            const monLoadingService: MonLoadingService = injector.get(MonLoadingService);
            monLoadingService.hide();
        },
    },
];
